import React, { useEffect, useState, useRef } from 'react';
import axios from 'axios';
import { logger } from '@fiverr-private/obs';
import { API, MODES } from '../../utils';
import { count } from '../../utils/sendStats';
import { configTransformer, type TransformedMenuProps } from '../../transformer';
import type { Currency } from '../../types';
import Scroller from '../Scroller';
import MenuPanel from './MenuPanel';

const METRIC_PREFIX = 'navigation_menu';

interface NavigationMenuProps extends TransformedMenuProps {
    currency: Currency;
}

const NavigationMenu = ({
    config: configProp,
    theme,
    mode = MODES.DEFAULT,
    locale,
    shouldPreRender = true,
    currency,
}: NavigationMenuProps) => {
    const [config, setConfig] = useState(configProp);
    const configWasFetchedRef = useRef(false);

    useEffect(() => {
        count(`${METRIC_PREFIX}.should_pre_render.${shouldPreRender}`);
        const hasFullConfig = !!configProp.level_2;

        if (configWasFetchedRef.current || shouldPreRender || hasFullConfig) {
            return;
        }

        axios
            .get(API.FETCH_NAVIGATION_CONFIG)
            .then((res) => {
                const { config: fetchedConfig } = res.data;
                setConfig((current) => ({
                    ...current,
                    ...configTransformer(fetchedConfig, locale, mode),
                }));
                count(`${METRIC_PREFIX}.ajax.lazy.succeeded`);
                configWasFetchedRef.current = true;
            })
            .catch((error) => {
                error.description = 'Failed to fetch category navigation items';
                logger.error(error);
                count(`${METRIC_PREFIX}.ajax.lazy.failed`);
            });
    }, [configProp.level_2, locale, mode, shouldPreRender]);

    useEffect(() => {
        setConfig(configProp);
    }, [configProp]);

    const { level_1 = {} } = config;

    return (
        <Scroller classname={theme} mode={mode} currency={currency} ctaSource="none">
            {level_1[0]?.map((panel) => (
                <MenuPanel
                    key={panel.id}
                    mode={mode}
                    item={panel}
                    config={config}
                    shouldPreRender={shouldPreRender}
                    eventTracker={{ vertical: panel.displayName, entry: panel.displayName }}
                />
            ))}
        </Scroller>
    );
};

export default NavigationMenu;
